import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../store/portal-auth';
import { portalTickets, portalCategories } from '../../lib/portal-api';
import { clsx } from 'clsx';

const STATUS_LABELS: Record<string, string> = {
  NY: 'Ny', UNDER_ARBEID: 'Under arbeid', VENTER_KUNDE: 'Venter kunde',
  VENTER_INTERNT: 'Venter internt', LOST: 'Løst', LUKKET: 'Lukket',
};
const STATUS_COLORS: Record<string, string> = {
  NY: 'bg-blue-100 text-blue-700', UNDER_ARBEID: 'bg-amber-100 text-amber-700',
  VENTER_KUNDE: 'bg-purple-100 text-purple-700', VENTER_INTERNT: 'bg-orange-100 text-orange-700',
  LOST: 'bg-green-100 text-green-700', LUKKET: 'bg-slate-100 text-slate-600',
};

function formatSakNr(n: number): string { return `SAK-${String(n).padStart(4, '0')}`; }
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PortalPage() {
  const { customer, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const { data: tickets, isLoading } = useQuery({
    queryKey: ['portal-tickets'],
    queryFn: portalTickets.list,
    refetchInterval: 30_000,
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-brand">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-white"><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" /><path d="M3 7l2-4h14l2 4" /><path d="M3 7h18" /></svg>
            </div>
            <span className="font-semibold text-slate-800">Kundeportal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">{customer?.navn || customer?.epost}</span>
            <button onClick={logout} className="btn-ghost text-sm">Logg ut</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div><h1 className="text-xl font-semibold text-slate-800">Mine saker</h1><p className="text-sm text-slate-500">Velkommen, {customer?.navn || customer?.epost}</p></div>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
            Ny sak
          </button>
        </div>
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400"><span className="animate-pulse">Laster saker…</span></div>
        ) : tickets?.length === 0 ? (
          <div className="card flex flex-col items-center justify-center p-12 text-center">
            <svg className="mb-3 h-12 w-12 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>
            <p className="text-sm font-medium text-slate-600">Ingen saker enda</p>
            <p className="mt-1 text-sm text-slate-400">Opprett din første sak med knappen over.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets?.map((t) => (
              <button key={t.id} onClick={() => navigate(`/portal/ticket/${t.id}`)} className="card flex w-full items-center justify-between p-4 text-left transition-colors hover:border-brand-300">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{formatSakNr(t.sakNr)}</span>
                    <span className={clsx('chip text-xs', STATUS_COLORS[t.status])}>{STATUS_LABELS[t.status]}</span>
                  </div>
                  <p className="mt-1 truncate text-sm font-medium text-slate-800">{t.tittel}</p>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                    <span>{formatDate(t.opprettet)}</span>
                    {t.kategori && <span>{t.kategori}</span>}
                    {t._count?.messages ? <span>{t._count.messages} meldinger</span> : null}
                  </div>
                </div>
                <svg className="h-4 w-4 shrink-0 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            ))}
          </div>
        )}
      </main>
      {showCreate && <CreateTicketModal onClose={() => setShowCreate(false)} onCreated={(id) => navigate(`/portal/ticket/${id}`)} />}
    </div>
  );
}

function CreateTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [tittel, setTittel] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [kategori, setKategori] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: categories } = useQuery({ queryKey: ['portal-categories'], queryFn: portalCategories.list });
  const createMutation = useMutation({
    mutationFn: () => portalTickets.create({ tittel: tittel.trim(), beskrivelse: beskrivelse.trim() || undefined, kategori: kategori || undefined }),
    onSuccess: (ticket) => { qc.invalidateQueries({ queryKey: ['portal-tickets'] }); onCreated(ticket.id); },
    onError: (err: any) => { setError(err?.message ?? 'Kunne ikke opprette sak.'); },
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Ny sak</h2>
          <button onClick={onClose} className="btn-ghost p-1"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (tittel.trim()) createMutation.mutate(); }} className="space-y-4">
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Tittel *</label><input type="text" required value={tittel} onChange={(e) => setTittel(e.target.value)} placeholder="Kort beskrivelse av saken" className="input" autoFocus /></div>
          {categories && categories.length > 0 && (
            <div><label className="mb-1 block text-sm font-medium text-slate-700">Kategori</label><select value={kategori} onChange={(e) => setKategori(e.target.value)} className="input"><option value="">— Velg kategori —</option>{categories.map((c) => <option key={c.id} value={c.navn}>{c.navn}</option>)}</select></div>
          )}
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Beskrivelse</label><textarea value={beskrivelse} onChange={(e) => setBeskrivelse(e.target.value)} placeholder="Beskriv problemet eller henvendelsen ditt…" rows={5} className="input resize-y" /></div>
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Avbryt</button>
            <button type="submit" disabled={createMutation.isPending || !tittel.trim()} className="btn-primary">{createMutation.isPending ? 'Oppretter…' : 'Opprett sak'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
